import { useState } from "react";
import { paths, API_URL } from "../config/endpoints";
import { UseBookPayload } from "../types/book/useBook-payload";
import { UseBookResponse } from "../types/book/useBook-res";
import { Reservation } from "../types/reservation";
import { ErrorResponse } from "../types/errors";

export class ReservationError {
  public errorResponse: ErrorResponse | null = null;
  public phase: "book" | "payment";

  constructor(phase: "book" | "payment", errorResponse: ErrorResponse | null) {
    this.phase = phase;
    this.errorResponse = errorResponse;
  }
}

export default function useCreateReservation(uid: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ReservationError | null>(null);
  const [data, setData] = useState<UseBookResponse | null>(null);

  const createReservation = async (payload: UseBookPayload) => {
    console.log("Starting createReservation with payload:", payload); // Log payload
    console.log(
      "Payment Intent Amount in createReservation:",
      payload.paymentIntentAmount
    ); // Log paymentIntentAmount
    setLoading(true);
    setError(null);

    try {
      const {
        paymentSuccessUrl,
        paymentCancelUrl,
        paymentProvider,
        paymentIntentAmount,
        travelInsuranceProduct = undefined,
        ...bookingPayload
      } = payload;

      // Step 1: Create Reservation
      const apiUrlBook = `${API_URL}/${paths.guest}/${paths.properties}/${uid}/${paths.book}`;

      const responseBook = await fetch(apiUrlBook, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...bookingPayload,
          paymentIntentAmount,
          travelInsuranceProduct,
        }),
      });

      console.log("Response status for booking:", responseBook.status); // Log response status

      if (!responseBook.ok) {
        const errorResponse = await responseBook.json();
        console.error("Error during reservation creation:", errorResponse);
        throw new ReservationError("book", errorResponse);
      }

      const reservation: Reservation = await responseBook.json();
      console.log("Reservation created successfully:", reservation); // Log reservation details

      // Step 2: Process Payment
      const apiUrlPayStripe = `${API_URL}/${paths.guest}/${paths.reservations}/${reservation.uid}/${reservation.referenceCode}/${paths.payWith}/${paths.stripe}/${paths.startSession}`;
      const apiUrlPayLynnbrook = `${API_URL}/${paths.guest}/${paths.reservations}/${reservation.uid}/${reservation.referenceCode}/${paths.payWith}/${paths.lynnbrook}`;
      const apiUrlPayAuthorizeNet = `${API_URL}/${paths.guest}/${paths.reservations}/${reservation.uid}/${reservation.referenceCode}/${paths.payWith}/${paths.lynnbrook}`;

      const apiUrlPay =
        paymentProvider === "lynnbrook"
          ? apiUrlPayLynnbrook
          : paymentProvider === "authorize-net"
          ? apiUrlPayAuthorizeNet
          : apiUrlPayStripe;

      console.log("Payment API URL:", apiUrlPay); // Log payment API URL

      const responsePay = await fetch(apiUrlPay, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          paymentSuccessUrl,
          paymentCancelUrl,
        }),
      });

      console.log("Response status for payment:", responsePay.status); // Log response status

      if (!responsePay.ok) {
        const errorResponse = await responsePay.json();
        console.error("Error during payment processing:", errorResponse);
        throw new ReservationError("payment", errorResponse);
      }

      const sessionUrl = await responsePay.json();
      console.log("Session URL received:", sessionUrl); // Log session URL

      // Set Data and Return
      const result = {
        reservation,
        sessionUrl:
          paymentProvider === "lynnbrook"
            ? sessionUrl.redirectTo
            : sessionUrl.sessionUrl,
      };

      setData(result);
      return result;
    } catch (err) {
      console.error("Error in createReservation:", err); // Log any errors
      if (err instanceof ReservationError) {
        setError(err);
      } else {
        setError(new ReservationError("book", null));
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { createReservation, loading, error, data };
}
